// Journal des échanges d'une affaire (conception du suivi des affaires, § 5) : ce qui s'est passé, avec qui, et
// si la personne a répondu. Rien n'est noté sans un clic ; la base fixe l'auteur, passe une relance à « a répondu »
// quand la personne écrit, et fait monter la relation.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Linkedin,
  Mail,
  MapPin,
  MessageSquarePlus,
  Phone,
  FileText,
  Users,
} from "lucide-react";
import {
  type Identifier,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { ActivityKind, Deal, DealActivity, ReplyStatus } from "../types";
import {
  CLEARED_ACTION,
  clearsOnReply,
  type ProposedAction,
  proposeFollowUp,
  today,
} from "./nextAction";
import { useSaveNextAction } from "./NextActionEditor";
import { formatDay } from "./projectUtils";
import { useDealPipeline } from "./usePartnerOrg";

export type JournalContact = { id: number; name: string };

const KINDS: ActivityKind[] = [
  "email",
  "linkedin",
  "call",
  "meeting",
  "site_visit",
  "documentation",
];
const ICONS: Record<ActivityKind, typeof Mail> = {
  email: Mail,
  linkedin: Linkedin,
  call: Phone,
  meeting: Users,
  site_visit: MapPin,
  documentation: FileText,
};
// Seuls ces échanges attendent une réponse ; un appel ou un rendez-vous est un fait accompli.
const AWAITS_REPLY: ActivityKind[] = ["email", "linkedin", "documentation"];

export const activitiesKey = (dealId: Identifier) => [
  "deal_activities",
  dealId,
];

export const useDealActivities = (dealId: Identifier | undefined) =>
  useQuery({
    queryKey: activitiesKey(dealId ?? ""),
    enabled: dealId != null,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("deal_activities")
        .select("*")
        .eq("deal_id", dealId!)
        .order("occurred_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DealActivity[];
    },
  });

export type LogInput = {
  deal_id: Identifier;
  contact_id: number | null;
  kind: ActivityKind;
  direction: "out" | "in";
  note?: string | null;
};

export const useLogActivity = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const log = async (input: LogInput) => {
    const { data, error } = await getSupabaseClient()
      .from("deal_activities")
      .insert({
        ...input,
        reply_status:
          input.direction === "out" && AWAITS_REPLY.includes(input.kind)
            ? "pending"
            : null,
      })
      .select()
      .single();
    if (error) {
      notify(error.message, { type: "error" });
      return null;
    }
    queryClient.invalidateQueries({ queryKey: activitiesKey(input.deal_id) });
    queryClient.invalidateQueries({ queryKey: ["contact_relations"] });
    queryClient.invalidateQueries({ queryKey: ["my_day"] });
    notify("resources.deals.journal.saved", { type: "info" });
    return data as DealActivity;
  };
  return { log };
};

/** Proposition de relance après un message parti : un clic l'accepte. */
export const FollowUpOffer = ({
  deal,
  proposal,
  onClose,
}: {
  deal: Deal;
  proposal: ProposedAction;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { save, busy } = useSaveNextAction();
  const date = formatDay(proposal.date, locale);
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <span className="flex-1">
        {proposal.contactName
          ? translate("resources.deals.journal.follow_up_offer", {
              name: proposal.contactName,
              date,
            })
          : translate("resources.deals.journal.follow_up_company", { date })}
      </span>
      <Button
        size="sm"
        disabled={busy}
        onClick={async () => {
          if (
            await save(deal.id, {
              next_action_type: proposal.type,
              next_action_date: proposal.date,
              next_action: proposal.contactName,
              next_action_contact_id: proposal.contactId,
            })
          )
            onClose();
        }}
      >
        {translate("resources.deals.journal.accept")}
      </Button>
      <Button size="sm" variant="ghost" onClick={onClose}>
        {translate("resources.deals.journal.later")}
      </Button>
    </div>
  );
};

/** Bandeau : premier message parti sur une affaire « À qualifier » → « Premier contact », d'un clic. */
const StageOffer = ({ deal, onClose }: { deal: Deal; onClose: () => void }) => {
  const translate = useTranslate();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { dealStages } = useDealPipeline(deal);
  const next = dealStages[1];
  if (deal.stage !== dealStages[0]?.value || !next) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <span className="flex-1">
        {translate("resources.deals.journal.stage_offer", {
          stage: next.label,
        })}
      </span>
      <Button
        size="sm"
        onClick={async () => {
          const { error } = await getSupabaseClient()
            .from("deals")
            .update({ stage: next.value })
            .eq("id", deal.id);
          if (error) {
            notify(error.message, { type: "error" });
            return;
          }
          queryClient.invalidateQueries({ queryKey: ["deals"] });
          queryClient.invalidateQueries({ queryKey: ["my_day"] });
          onClose();
        }}
      >
        {translate("resources.deals.journal.stage_move")}
      </Button>
      <Button size="sm" variant="ghost" onClick={onClose}>
        {translate("resources.deals.journal.later")}
      </Button>
    </div>
  );
};

export const LogActivityDialog = ({
  deal,
  contacts,
  open,
  onOpenChange,
  onLogged,
  preset,
}: {
  deal: Deal;
  contacts: JournalContact[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogged: (activity: DealActivity) => void;
  preset?: Partial<LogInput>;
}) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) =>
    translate(`resources.deals.journal.${key}`, options);
  const { log } = useLogActivity();
  const [kind, setKind] = useState<ActivityKind>(preset?.kind ?? "email");
  const [direction, setDirection] = useState<"out" | "in">(
    preset?.direction ?? "out",
  );
  // Une personne du préremplissage absente de la liste ne peut pas être affichée : « la société ».
  const initialContact = () => {
    const wanted =
      preset && "contact_id" in preset ? preset.contact_id : contacts[0]?.id;
    return wanted != null && contacts.some((c) => c.id === wanted)
      ? String(wanted)
      : "";
  };
  const [contact, setContact] = useState<string>(initialContact);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // À chaque ouverture, repartir du préremplissage courant (type de l'action faite, personne visée).
  useEffect(() => {
    if (!open) return;
    setKind(preset?.kind ?? "email");
    setDirection(preset?.direction ?? "out");
    setContact(initialContact());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    setBusy(true);
    const activity = await log({
      deal_id: deal.id,
      contact_id: contact ? Number(contact) : null,
      kind,
      direction,
      note: note.trim() || null,
    });
    setBusy(false);
    if (activity) {
      setNote("");
      onOpenChange(false);
      onLogged(activity);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("log")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <ToggleGroup
            type="single"
            variant="outline"
            value={kind}
            onValueChange={(value) => value && setKind(value as ActivityKind)}
            className="flex-wrap justify-start"
          >
            {KINDS.map((value) => (
              <ToggleGroupItem key={value} value={value} size="sm">
                {t(`kinds.${value}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <ToggleGroup
            type="single"
            variant="outline"
            value={direction}
            onValueChange={(value) =>
              value && setDirection(value as "out" | "in")
            }
            className="justify-start"
          >
            <ToggleGroupItem value="out" size="sm">
              {t("direction_out")}
            </ToggleGroupItem>
            <ToggleGroupItem value="in" size="sm">
              {t("direction_in")}
            </ToggleGroupItem>
          </ToggleGroup>
          <label className="flex flex-col gap-1 text-sm">
            {t("person")}
            <select
              className="rounded-md border bg-background px-2 py-1.5"
              value={contact}
              onChange={(event) => setContact(event.target.value)}
            >
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              <option value="">{t("company")}</option>
            </select>
          </label>
          <Input
            value={note}
            maxLength={500}
            placeholder={t("note")}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button disabled={busy} onClick={submit}>
            {t("save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Pastille de l'état d'un message : un clic pour dire s'il a répondu. */
const ReplyChip = ({
  activity,
  deal,
  onReplied,
}: {
  activity: DealActivity;
  deal: Deal;
  onReplied: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { save } = useSaveNextAction();
  if (!activity.reply_status) return null;
  const set = async (status: ReplyStatus) => {
    // Par la base : qui voit l'affaire règle l'état d'un message, même noté par un collègue. Rien n'est effacé ni
    // ouvert si elle refuse.
    const { error } = await getSupabaseClient().rpc("set_reply_status", {
      activity: activity.id,
      status,
    });
    if (error) {
      notify(error.message, { type: "error" });
      return;
    }
    queryClient.invalidateQueries({ queryKey: activitiesKey(deal.id) });
    queryClient.invalidateQueries({ queryKey: ["contact_relations"] });
    if (status === "replied") {
      // La relance vers cette personne n'a plus lieu d'être ; l'utilisateur prévoit la suite.
      if (
        clearsOnReply(deal, activity.contact_id) &&
        !(await save(deal.id, CLEARED_ACTION))
      )
        return;
      onReplied();
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button">
          <Badge
            variant={
              activity.reply_status === "replied" ? "default" : "outline"
            }
          >
            {translate(
              `resources.deals.journal.reply.${activity.reply_status}`,
            )}
          </Badge>
        </button>
      </DropdownMenuTrigger>
      {/* Le focus ne revient pas à la pastille : il refermerait l'éditeur que « A répondu » vient d'ouvrir. */}
      <DropdownMenuContent
        align="end"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {(["replied", "no_reply", "declined", "pending"] as ReplyStatus[]).map(
          (status) => (
            <DropdownMenuItem key={status} onClick={() => set(status)}>
              {translate(`resources.deals.journal.reply.${status}`)}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DealJournal = ({
  deal,
  contacts,
  onAskNext,
  canWrite = true,
}: {
  deal: Deal;
  contacts: JournalContact[];
  // Après « a répondu » ou « Fait » : ouvrir l'éditeur de prochaine action.
  onAskNext?: () => void;
  canWrite?: boolean;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { data: activities } = useDealActivities(deal.id);
  const [open, setOpen] = useState(false);
  const [proposal, setProposal] = useState<ProposedAction | null>(null);
  const [stageOffer, setStageOffer] = useState(false);
  const names = new Map(contacts.map((c) => [c.id, c.name]));
  const { save } = useSaveNextAction();

  const logged = async (activity: DealActivity) => {
    if (activity.direction === "in") {
      // Même suite que la pastille « A répondu » : la relance devient sans objet, l'utilisateur prévoit la suite.
      if (
        clearsOnReply(deal, activity.contact_id) &&
        !(await save(deal.id, CLEARED_ACTION))
      )
        return;
      onAskNext?.();
      return;
    }
    const contact =
      activity.contact_id != null
        ? {
            id: Number(activity.contact_id),
            name: names.get(Number(activity.contact_id)) ?? "",
          }
        : null;
    if (activity.reply_status === "pending")
      setProposal(proposeFollowUp(deal, contact, today()));
    // Premier message parti : proposer l'étape suivante (vérifié par StageOffer).
    if (!activities?.some((a) => a.direction === "out")) setStageOffer(true);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">
          {translate("resources.deals.journal.title")}
        </h4>
        {canWrite && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <MessageSquarePlus className="size-4" />
            {translate("resources.deals.journal.log")}
          </Button>
        )}
      </div>
      {proposal && (
        <FollowUpOffer
          deal={deal}
          proposal={proposal}
          onClose={() => setProposal(null)}
        />
      )}
      {stageOffer && (
        <StageOffer deal={deal} onClose={() => setStageOffer(false)} />
      )}
      {!activities?.length ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.deals.journal.empty")}
        </p>
      ) : (
        <ul className="flex flex-col divide-y text-sm">
          {activities.map((activity) => {
            const Icon = ICONS[activity.kind];
            return (
              <li key={activity.id} className="flex items-center gap-2 py-1.5">
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 min-w-0 truncate">
                  {activity.direction === "in" ? "← " : "→ "}
                  {activity.contact_id != null
                    ? (names.get(Number(activity.contact_id)) ??
                      translate("resources.deals.journal.person"))
                    : translate("resources.deals.journal.company")}
                  {activity.note && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {activity.note}
                    </span>
                  )}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDay(activity.occurred_at, locale)}
                </span>
                {canWrite ? (
                  <ReplyChip
                    activity={activity}
                    deal={deal}
                    onReplied={() => onAskNext?.()}
                  />
                ) : (
                  activity.reply_status && (
                    <Badge variant="outline">
                      {translate(
                        `resources.deals.journal.reply.${activity.reply_status}`,
                      )}
                    </Badge>
                  )
                )}
              </li>
            );
          })}
        </ul>
      )}
      {canWrite && (
        <LogActivityDialog
          deal={deal}
          contacts={contacts}
          open={open}
          onOpenChange={setOpen}
          onLogged={logged}
        />
      )}
    </div>
  );
};
